import { Controller } from "@hotwired/stimulus"

// Handles open/close for dropdown menus with smooth enter/exit animations.
// - Closes on click outside
// - Closes all other dropdowns when one opens (mutual exclusion)
// - Uses CSS keyframe animations for polished enter/exit
export default class extends Controller {
  // trigger is optional: only menus opened by a button that has to report its
  // state (the landing nav's Tools menu) declare one.
  static targets = ["menu", "trigger"]

  connect() {
    this._outsideClick = this._handleOutsideClick.bind(this)
    this._closeAll     = () => this.close()
    this._isOpen       = false
    document.addEventListener("dropdown:closeAll", this._closeAll)
  }

  disconnect() {
    document.removeEventListener("click",            this._outsideClick)
    document.removeEventListener("dropdown:closeAll", this._closeAll)
    this._clearExitListener()
  }

  toggle(event) {
    event.stopPropagation()
    this._isOpen ? this.close() : this._open()
  }

  close() {
    if (!this.hasMenuTarget || !this._isOpen) return
    this._isOpen = false
    this._syncTrigger(false)
    document.removeEventListener("click", this._outsideClick)

    const menu = this.menuTarget
    menu.classList.remove("dropdown-enter")
    menu.classList.add("dropdown-exit")

    // After the exit animation finishes, apply the hidden state
    this._clearExitListener()
    this._onExitEnd = () => {
      menu.classList.remove("dropdown-exit")
      menu.classList.add("dropdown-hidden")
      this._clearExitListener()
    }
    menu.addEventListener("animationend", this._onExitEnd, { once: true })
  }


  _open() {
    if (!this.hasMenuTarget) return

    // Close every other dropdown first
    document.dispatchEvent(new CustomEvent("dropdown:closeAll"))

    const menu = this.menuTarget
    this._clearExitListener()
    menu.classList.remove("dropdown-hidden", "dropdown-exit")
    menu.classList.add("dropdown-enter")
    this._isOpen = true
    this._syncTrigger(true)

    // Defer so this click doesn't immediately re-close via the outside handler
    setTimeout(() => document.addEventListener("click", this._outsideClick), 0)
  }

  _syncTrigger(expanded) {
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", String(expanded))
  }

  _handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  _clearExitListener() {
    if (this._onExitEnd) {
      this.menuTarget.removeEventListener("animationend", this._onExitEnd)
      this._onExitEnd = null
    }
  }
}
